import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <section id="categories" className="scroll-mt-20 bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              {t("categories.title")}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t("categories.subtitle")}
            </p>
          </div>

          {/* Dropdown */}
          <div ref={ref} className="relative">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-200 cursor-pointer md:w-auto"
            >
              {t("categories.dropdown.label")}
              <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
                <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
              </span>
            </button>

            {open && (
              <div className="absolute right-0 z-40 mt-2 max-h-80 w-full overflow-y-auto rounded-lg border border-background-300/70 bg-background-50 py-1 md:w-72">
                <p className="px-3 py-2 text-xs text-foreground-400">
                  {t("categories.dropdown.hint")}
                </p>
                {categories.map((c) => (
                  <a
                    key={c.key}
                    href={c.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 whitespace-nowrap px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                  >
                    <img src={c.image} alt={t(`cat.${c.key}`)} className="h-7 w-7 rounded object-cover" />
                    {t(`cat.${c.key}`)}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category grid */}
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-7">
          {categories.map((c) => (
            <a
              key={c.key}
              href={c.url}
              target="_blank"
              rel="noopener nofollow"
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-300"
            >
              <div className="relative h-28 w-full overflow-hidden">
                <img
                  src={c.image}
                  alt={t(`cat.${c.key}`)}
                  title={`${t(`cat.${c.key}`)} — USFans Spreadsheet`}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between gap-2 border-t border-background-100 bg-background-50 px-3 py-2.5">
                <span className="truncate text-sm font-semibold text-foreground-900">
                  {t(`cat.${c.key}`)}
                </span>
                <span className="flex h-4 w-4 shrink-0 items-center justify-center text-primary-600 opacity-0 transition-opacity group-hover:opacity-100">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}